import { OccupancyType, ProspectDocument } from '@common/entities';
import { get, post } from 'config/api/config';

import { AffordableMemberTypes } from 'types/fields/block-fields/affordable-member-fields';

const DOCUMENT_CONTROLLER = '/viewer/document';
const POLICY_DOCUMENT_ENDPOINT = `${DOCUMENT_CONTROLLER}/policy-documents`;
const PROSPECT_DOCUMENT_ENDPOINT = `${DOCUMENT_CONTROLLER}/prospect-documents`;
const GENERATE_POLICY_DOCUMENTS_ENDPOINT = `${DOCUMENT_CONTROLLER}/policy-documents/generate`;

export const generatePolicyDocuments = async () => {
  // eslint-disable-next-line no-console
  console.log('Generating policy documents...');
  const response = await post(GENERATE_POLICY_DOCUMENTS_ENDPOINT);
  return response;
};

export const getPolicyDocument = async (fileAssociationId: number) => {
  const response = await get(`${POLICY_DOCUMENT_ENDPOINT}/${fileAssociationId}`, {
    responseType: 'arraybuffer',
  });
  return response;
};

export const getPolicyDocumentForSecondaryApplicant = async (
  fileAssociationId: number,
  email: string,
) => {
  const response = await get(
    `${POLICY_DOCUMENT_ENDPOINT}/secondary/${fileAssociationId}/${email}`,
    {
      responseType: 'arraybuffer',
    },
  );
  return response;
};

export const getPolicyDocuments = async (email?: string) => {
  const url = email ? `${POLICY_DOCUMENT_ENDPOINT}?email=${email}` : POLICY_DOCUMENT_ENDPOINT;

  const response = await get(url);
  return response;
};

export const getPolicyDocumentsForSecondaryApplicant = async (email: string) => {
  const response = await get(`${POLICY_DOCUMENT_ENDPOINT}/secondary/${email}`);
  return response;
};

export const signPolicyDocument = async (fileAssociationId: number, initials: string) => {
  const response = await post(`${POLICY_DOCUMENT_ENDPOINT}/sign/${fileAssociationId}/${initials}`);
  return response;
};

export const signPolicyDocumentForSecondaryApplicant = async (
  fileAssociationId: number,
  initials: string,
  email: string,
) => {
  const response = await get(
    `${POLICY_DOCUMENT_ENDPOINT}/sign/secondary/${fileAssociationId}/${initials}/${email}`,
  );
  return response;
};

export const getProspectDocuments = async () => {
  const response = await get<ProspectDocument[]>(PROSPECT_DOCUMENT_ENDPOINT);
  return response?.data;
};

export const getProspectDocument = async (documentId: number) => {
  if (!documentId) throw new Error('Document Id is missing.');

  const response = await get(`${PROSPECT_DOCUMENT_ENDPOINT}/${documentId}`, {
    responseType: 'arraybuffer',
  });
  return response;
};

export const signProspectDocument = async (
  documentId: number,
  fullName: string,
  childFileId?: number,
) => {
  const body: any = {
    documentId,
    fullName,
  };
  if (childFileId) {
    body.childFileId = childFileId;
  }

  const response = await post(`${PROSPECT_DOCUMENT_ENDPOINT}/signDocument/${documentId}`, body);
  return response;
};

interface AffordableMemberGenerateDocumentRequest {
  documentId: number[];
  type: AffordableMemberTypes;
}
interface AffordableMemberGenerateDocumentResponse {
  status: number;
  message: string;
  total_documents: number;
  successful_documents: number;
}

export const generateDocument = async (
  data: AffordableMemberGenerateDocumentRequest[],
  occupancyType = OccupancyType.Conventional,
) => {
  const response = await post<AffordableMemberGenerateDocumentResponse>(
    `${DOCUMENT_CONTROLLER}/${occupancyType}/generate`,
    data,
  );
  return response?.data;
};

type AffordableDocumentsResponse = {
  typeId: AffordableMemberTypes;
  referenceId: null;
  documentId: number;
  documentName: string;
  fileAssociationId: number;
  fileId: number;
  fileSignedDate: string | null;
};
type AffordableMemberDocumentsResponse = Record<
  AffordableMemberTypes,
  AffordableDocumentsResponse[]
>;
export const getGeneratedAffordableDocuments = async () => {
  const response = await get<AffordableMemberDocumentsResponse>(
    `${DOCUMENT_CONTROLLER}/affordable/documents`,
  );
  return response?.data;
};

export const signAffordableDocument = async (fileAssociationId: number) => {
  const response = await post<AffordableMemberDocumentsResponse>(
    `${DOCUMENT_CONTROLLER}/affordable/sign-document/${fileAssociationId}`,
  );
  return response?.data;
};
